import { useMemo, useState } from 'react'
import { toNumber } from '@/lib/decimal'
import { formatContractHours } from '@/lib/format'
import type { MonthKey, PersonRow, PlanningCell, PlanningOverview } from '@/lib/planning-types'
import { cn } from '@/lib/utils'
import { CapacityBar, capacitySummary } from './CapacityBar'
import { EditableHours } from './EditableHours'
import { monthLabel } from './month'
import { PersonMonthPanel } from './PersonMonthPanel'
import { PlanningLegend } from './PlanningLegend'

interface MonthColumnsProps {
  overview: PlanningOverview
  /** Alleen op doorreis naar het persoonspaneel; de kolommen zelf tonen geen geld. */
  moneyOpen: boolean
  onToggleMoney: () => void
  onSetCapacity: (personId: string, month: MonthKey, hours: string | null) => void
  onOpenOpportunity: (id: string) => void
}

/** Een aangeklikte cel: welke persoon in welke maand. */
interface Selection {
  personId: string | null
  displayName: string
  month: MonthKey
}

/**
 * Het antwoord op de vraag waar dit scherm voor is: per maand, per persoon, hoe
 * vol het zit.
 *
 * Eén kaart met interne hairline-scheiding en niet een raster van losse tegels;
 * dat laatste is precies wat het designsysteem niet wil. Bij drie maanden staan
 * de kolommen naast elkaar, daaronder klappen ze onder elkaar.
 */
export function MonthColumns({ overview, moneyOpen, onToggleMoney, onSetCapacity, onOpenOpportunity }: MonthColumnsProps) {
  const [selected, setSelected] = useState<Selection | null>(null)

  // Wijst de selectie nergens meer naar (venster verschoven, persoon weg), dan is
  // er geen selectie.
  const cell = selected
    ? (selected.personId === null
        ? overview.unassigned.months
        : (overview.persons.find((person) => person.personId === selected.personId)?.months ?? [])
      ).find((month) => month.month === selected.month)
    : undefined

  const toggle = (next: Selection) =>
    setSelected((current) =>
      current && current.personId === next.personId && current.month === next.month ? null : next,
    )

  // Wie het drukst is staat boven, over het hele venster geteld en dus in elke
  // kolom in dezelfde volgorde: anders springt iemand per maand van plek en is
  // een rij niet meer horizontaal te volgen. Gelijk aantal uren blijft op naam.
  const persons = useMemo(() => {
    const planned = (person: PersonRow) =>
      person.months.reduce((total, month) => total + toNumber(month.plannedHours), 0)

    return [...overview.persons].sort(
      (a, b) => planned(b) - planned(a) || a.displayName.localeCompare(b.displayName, 'nl'),
    )
  }, [overview.persons])

  return (
    <>
      <div
        className={cn(
          'grid divide-y divide-hairline lg:divide-x lg:divide-y-0',
          overview.months.length <= 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-3',
          overview.months.length > 3 && 'lg:grid-cols-3 xl:grid-cols-4',
        )}
      >
        {overview.months.map((month, index) => (
          <MonthColumn
            key={month}
            overview={overview}
            persons={persons}
            month={month}
            index={index}
            onSetCapacity={onSetCapacity}
            selected={selected}
            onSelect={toggle}
          />
        ))}
      </div>

      <PlanningLegend months={overview.team} />

      {selected && cell ? (
        <PersonMonthPanel
          key={`${selected.personId ?? 'unassigned'}|${selected.month}`}
          overview={overview}
          personId={selected.personId}
          displayName={selected.displayName}
          month={selected.month}
          cell={cell}
          moneyOpen={moneyOpen}
          onToggleMoney={onToggleMoney}
          onClose={() => setSelected(null)}
          onOpenOpportunity={onOpenOpportunity}
        />
      ) : null}
    </>
  )
}

function MonthColumn({
  overview,
  persons,
  month,
  index,
  onSetCapacity,
  selected,
  onSelect,
}: {
  overview: PlanningOverview
  persons: PersonRow[]
  month: MonthKey
  index: number
  onSetCapacity: MonthColumnsProps['onSetCapacity']
  selected: Selection | null
  onSelect: (selection: Selection) => void
}) {
  const team = overview.team[index]
  const unassigned = overview.unassigned.months[index]
  const undistributed = overview.undistributed.months[index]
  const free = team?.freeHours === null || team?.freeHours === undefined ? null : toNumber(team.freeHours)

  return (
    <section className="px-5 py-4" data-testid="month-column">
      {/*
        De maand opent met het oordeel over de hele maand en pas daarna met wie
        hem vult: eerst weten of het past, dan pas bij wie het vandaan komt.
      */}
      <header className="flex items-baseline justify-between gap-3">
        {/* Zonder jaartal: dat staat al in de kop van de kaart en zou hier drie keer herhalen. */}
        <h3 className="text-sm font-semibold capitalize text-ink">{monthName(month)}</h3>
        {team ? (
          <span className={cn('num text-xs', loadTone(team))}>
            {formatContractHours(toNumber(team.plannedHours))}
            <span className="text-ink-faint">
              {' / '}
              {team.capacityHours === null ? '-' : formatContractHours(toNumber(team.capacityHours))} u
            </span>
          </span>
        ) : null}
      </header>

      {team ? (
        <div className="mt-2" data-testid="team-bar">
          <span className="flex">
            <CapacityBar cell={team} size="team" />
          </span>
          <p className="mt-1.5 text-xs text-ink-faint">
            zeker {formatContractHours(toNumber(team.committedHours))} · verwacht{' '}
            {formatContractHours(toNumber(team.uncertainHours))}
            {free === null ? null : (
              <span className={cn(free < 0 && 'text-status-overdue')}>
                {' · '}
                {free >= 0 ? `${formatContractHours(free)} u vrij` : `${formatContractHours(-free)} u te veel`}
              </span>
            )}
          </p>
        </div>
      ) : null}

      <ul className="mt-3 space-y-2.5 border-t border-hairline pt-3">
        {persons.map((person) => (
          <PersonLine
            key={person.personId}
            name={person.displayName}
            cell={person.months[index]}
            onSetCapacity={(hours) => onSetCapacity(person.personId, month, hours)}
            selected={selected?.personId === person.personId && selected.month === month}
            onSelect={() => onSelect({ personId: person.personId, displayName: person.displayName, month })}
          />
        ))}
        {toNumber(unassigned?.plannedHours) > 0 ? (
          <PersonLine
            name="Nog niet toegewezen"
            cell={unassigned}
            muted
            selected={selected?.personId === null && selected.month === month}
            onSelect={() => onSelect({ personId: null, displayName: 'Nog niet toegewezen', month })}
          />
        ) : null}
        {/* Omvang die nog nergens staat. Geen klikbare regel: er valt niets uit te
            klappen, want deze uren horen bij geen enkele maand in het bijzonder. */}
        {toNumber(undistributed?.plannedHours) > 0 ? (
          <PersonLine name="Onverdeeld" cell={undistributed} muted />
        ) : null}
      </ul>
    </section>
  )
}

function PersonLine({
  name,
  cell,
  onSetCapacity,
  muted,
  selected,
  onSelect,
}: {
  name: string
  cell: PlanningCell | undefined
  onSetCapacity?: (hours: string | null) => void
  muted?: boolean
  selected?: boolean
  onSelect?: () => void
}) {
  if (!cell) return null

  const planned = toNumber(cell.plannedHours)
  const committed = toNumber(cell.committedHours)
  const free = cell.freeHours === null ? null : toNumber(cell.freeHours)
  // Een regel zonder werk en zonder capaciteitsbasis heeft niets te vertellen; die
  // is geen knop. Het beschikbaar-getal rechts blijft wel altijd invulbaar.
  const openable = onSelect !== undefined && (planned > 0 || cell.capacityHours !== null)

  const label = (
    <span className={cn('block truncate pr-[132px] text-left text-sm', muted ? 'text-ink-soft' : 'text-ink')}>{name}</span>
  )

  /*
   * Naam en getallen op de bovenste regel, de balk daaronder over de volle
   * kolombreedte. Naast elkaar hield de balk een centimeter of twee over, en op
   * die breedte vallen vier banden niet uit elkaar te houden. De getallen staan
   * buiten de knop omdat het beschikbaar-getal zelf een invoerveld is; een knop
   * in een knop bestaat niet.
   */
  return (
    <li className="relative">
      {openable ? (
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          title={`${name} in ${monthLabel(cell.month)}: ${capacitySummary(cell)}. Bekijk waar deze uren vandaan komen`}
          className={cn(
            '-mx-1 block w-[calc(100%+0.5rem)] space-y-1 rounded-sm px-1 py-1 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
            selected ? 'bg-fill-2' : 'hover:bg-fill-1',
          )}
        >
          {label}
          <span className="flex">
            {/* De samenvatting staat in de title van de knop en dus in zijn
                beschrijving; hier nog eens als sr-only zou hem ook in de naam
                zetten en dan hoor je hem twee keer. */}
            <CapacityBar cell={cell} described />
          </span>
        </button>
      ) : (
        <div className="space-y-1 py-1" title={name}>
          {label}
          <span className="flex">
            <CapacityBar cell={cell} />
          </span>
        </div>
      )}

      <span className="absolute right-0 top-1 flex items-baseline justify-end gap-1">
        {free !== null ? (
          <span className="num mr-1 text-xs text-ink-faint">
            {free >= 0 ? `${formatContractHours(free)} vrij` : `${formatContractHours(-free)} over`}
          </span>
        ) : null}
        {planned > 0 ? (
          <>
            <span className={cn('num text-xs', loadTone(cell))}>
              {formatContractHours(committed === planned ? committed : planned)}
            </span>
            <span className="text-xs text-ink-faint">/</span>
          </>
        ) : null}
        {onSetCapacity ? (
          <EditableHours
            value={cell.capacityHours}
            derived={cell.capacitySource !== 'manual'}
            onCommit={(hours) => onSetCapacity(hours)}
            label={`Beschikbare uren van ${name} in ${monthLabel(cell.month)}`}
            title="Beschikbare uren; leeg laten valt terug op de contracturen"
            className="w-[40px] text-xs"
          />
        ) : (
          <span className="num w-[40px] text-right text-xs text-ink-faint">-</span>
        )}
      </span>
    </li>
  )
}

/**
 * Dezelfde rangorde als de markeringen in de balk, zodat het getal en de streep
 * nooit iets anders zeggen: rood pas voorbij de beschikbare uren, daaronder de
 * attentie-kleur voor wie alleen over zijn streven zit.
 */
function loadTone(cell: PlanningCell): string {
  if (cell.overbooked) return 'text-status-overdue'
  if (cell.overTarget) return 'text-status-approval'

  return 'text-ink-soft'
}

/** "Augustus", zonder jaartal. */
function monthName(month: MonthKey): string {
  return monthLabel(month).replace(/\s\d{4}$/, '')
}
